<template>
  <div class="guest-upgrade-prompt">
    <!-- Hero panel (rendered inline on the guest pages). The Upgrade button opens the
         confirmation modal rather than firing the upgrade directly, so the user gets a
         state-aware confirmation step before billing changes. -->
    <div v-if="mode === 'hero'" class="guest-upgrade-prompt-hero">
      <span class="guest-upgrade-prompt-hero__pill">Professional</span>
      <h2 class="guest-upgrade-prompt-hero__title">{{ title }}</h2>
      <p class="guest-upgrade-prompt-hero__body">
        {{ body }}
      </p>
      <dd-button
        v-if="canSelfServeUpgrade"
        color="blue"
        size="medium"
        @click="isInternalModalVisible = true"
      >Upgrade to Professional</dd-button>
      <p v-else class="guest-upgrade-prompt-hero__muted">
        Ask your account owner to upgrade.
      </p>
    </div>

    <!-- Confirmation modal. Single source of upgrade-action UX for both modes.
         In hero mode, opens when the user clicks Upgrade in the hero.
         In modal mode, opens when the parent flips :visible.sync. -->
    <dd-modal
      v-model:visible="modalVisible"
      :title="title"
      :self-close="false"
      :before-close="handleBeforeClose"
      desktop-sizing="small"
      append-to-body
      class="guest-upgrade-prompt__modal"
    >
      <p class="guest-upgrade-prompt__modal-body" v-html="modalBodyHtml" />
      <template #footer>
        <span>
          <dd-button :is-disabled="isSubmitting" type="text" @click="closeModal">{{ cancelLabel }}</dd-button>
          <dd-button
            v-if="canSelfServeUpgrade"
            :is-loading="isSubmitting"
            color="blue"
            @click="performUpgrade"
          >Upgrade to Professional</dd-button>
        </span>
      </template>
    </dd-modal>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

/* API */
import BillingAPI from '@/api/modules/billing.api'
import { useProfileStore } from '@/modules/profile/profile.store'

/* Store */

/**
 * Upgrade-to-Pro prompt for the Guests feature.
 *
 * mode='hero':   Renders an inline hero panel on the guest pages (Guests index, Invite Guest,
 *                project Add/Remove Guests). The Upgrade button opens an internal confirmation
 *                modal (state-aware copy) before any billing change.
 * mode='modal':  Renders only the modal; parent drives :visible.sync. Used by inline
 *                guest-invite affordances (e.g. the project quick-add picker).
 *
 * Confirmation modal copy branches on (canSelfServeUpgrade, isTrial):
 *   - trial:        "No charge during your trial."
 *   - paid owner:   "You'll be charged a prorated amount."
 *   - non-owner:    "Ask your account owner to upgrade." (no upgrade button)
 *
 * Upgrade flow mirrors seat-limit-modal: BillingAPI.post_UpgradeToProfessional with
 * targetSeats: null (Pro defaults), refresh profile + metrics so featureEnablement reflects
 * the change, then emit `upgraded` so callers can retry the gated action.
 */
export default defineComponent({
  name: 'GuestUpgradePrompt',

  props: {
    mode: {
      type: String,
      default: 'hero',
      validator: (val: string) => ['hero', 'modal'].includes(val),
    },
    /** Only honored when mode === 'modal'. Use with :visible.sync from the parent. */
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Guests are a Pro feature',
    },
    body: {
      type: String,
      default:
        'Bring outside collaborators into specific projects without giving them full access to your account. ',
    },
    lead: {
      type: String,
      default:
        'Upgrading unlocks Guests and the rest of the Professional feature set.',
    },
    noSelfUpgradeCopy: {
      type: String,
      default:
        'Guests let you bring outside collaborators into specific projects without giving them full access. ' +
        'Ask your account owner to upgrade to Pro.',
    },
  },

  emits: ['update:visible', 'upgraded'],

  data() {
    return {
      isSubmitting: false,
      isInternalModalVisible: false,
    }
  },

  computed: {
    /**
     * Two-way visibility binding. In modal mode reads/writes the parent's `visible` prop;
     * in hero mode reads/writes the internal flag the hero button toggles. This lets the
     * same <dd-modal> serve both modes without duplication.
     */
    modalVisible: {
      get(): boolean {
        return this.mode === 'modal'
          ? this.visible
          : this.isInternalModalVisible
      },
      set(newValue: boolean): void {
        if (this.mode === 'modal') {
          this.$emit('update:visible', newValue)
        } else {
          this.isInternalModalVisible = newValue
        }
      },
    },
    /**
     * Trial: anyone on the account can upgrade (no money moves until trial ends).
     * Post-trial: only the account owner can self-serve.
     */
    canSelfServeUpgrade(): boolean {
      return this.$permissions.isTrial || this.$permissions.isOwner
    },
    modalBodyHtml(): string {
      if (!this.canSelfServeUpgrade) {
        return this.noSelfUpgradeCopy
      }
      if (this.$permissions.isTrial) {
        return `${this.lead} <strong>No charge during your trial.</strong>`
      }
      return (
        `${this.lead} You'll be charged a prorated amount for the new plan.`
      )
    },
    cancelLabel(): string {
      return this.canSelfServeUpgrade ? 'Not now' : 'Got it'
    },
  },

  methods: {
    closeModal() {
      this.modalVisible = false
    },
    handleBeforeClose() {
      if (this.isSubmitting) {
        return
      }
      this.closeModal()
    },
    async performUpgrade() {
      this.isSubmitting = true
      try {
        await BillingAPI.post_UpgradeToProfessional({
          isSwitchingPeriod: false,
          targetSeats: null,
        })

        // Refresh profile so featureEnablement.canHaveGuests reflects the change in
        // any component reading from $permissions / the store. PROFILE_FETCH replaces
        // profile.data wholesale and does NOT carry profileMetrics, so we have to
        // refetch metrics too (matches seat-limit-modal).
        await Promise.all([
          useProfileStore().profileFetch(),
          useProfileStore().profileMetricsFetch(),
        ])

        this.$messaging.showSuccess(`You're on Professional now.`)
        this.$emit('upgraded')
        this.closeModal()
      } catch (error) {
        this.$error.emit(error, true)
      }
      this.isSubmitting = false
    },
  },
})
</script>

<style lang="scss">
.guest-upgrade-prompt {
  &__modal-body {
    @include lettering--p-md;
    margin: 0;
  }
}

.guest-upgrade-prompt-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 60px 30px;

  &__pill {
    display: inline-block;
    font-size: 11px;
    font-weight: $bold;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 4px 12px;
    border-radius: 999px;
    background-color: $color--blue-15;
    color: $color--blue-80;
    margin-bottom: 14px;
  }

  &__title {
    margin-top: 0;
  }

  &__body {
    @include lettering--p-md;
    max-width: 460px;
    margin: 12px 0 24px 0;
  }

  &__muted {
    @include lettering--p-sm;
    color: var(--color-text-third);
    margin-top: 16px;
  }
}
</style>
